<script lang="ts">
  import { user, SERVER } from '../lib/stores';
  import { listFiles } from '../lib/blossom';
  import { signEvent } from '../lib/auth';
  import { createDeleteAuth, encodeAuthorizationHeader } from 'blossom-client-sdk';

  interface BlobDescriptor {
    sha256: string;
    size: number;
    type: string;
    uploaded: number;
    expires_at?: number;
  }

  type SortKey = 'uploaded' | 'size' | 'type';
  type SortDir = 'asc' | 'desc';

  let files = $state<BlobDescriptor[]>([]);
  let loading = $state(true);
  let error = $state('');
  let sortKey = $state<SortKey>('uploaded');
  let sortDir = $state<SortDir>('desc');
  let confirmDeleteSha = $state<string | null>(null);
  let deleting = $state<string | null>(null);
  let copiedSha = $state<string | null>(null);
  let viewerFile = $state<BlobDescriptor | null>(null);

  let sortedFiles = $derived.by(() => {
    const sorted = [...files].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'uploaded') cmp = a.uploaded - b.uploaded;
      else if (sortKey === 'size') cmp = a.size - b.size;
      else if (sortKey === 'type') cmp = a.type.localeCompare(b.type);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  });

  function formatSize(bytes: number): string {
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(1) + ' GB';
    if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return bytes + ' B';
  }

  function relativeTime(timestamp: number): string {
    const seconds = Math.floor((Date.now() - timestamp * 1000) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return days === 1 ? '1 day ago' : `${days} days ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return months === 1 ? '1 month ago' : `${months} months ago`;
    const years = Math.floor(months / 12);
    return years === 1 ? '1 year ago' : `${years} years ago`;
  }

  function typeColor(mimeType: string): string {
    if (mimeType.startsWith('image/')) return '#58a6ff';
    if (mimeType.startsWith('video/')) return '#bc8cff';
    if (mimeType.startsWith('audio/')) return '#3fb950';
    if (mimeType.startsWith('text/')) return '#d29922';
    if (mimeType.startsWith('application/pdf')) return '#f85149';
    return '#8b949e';
  }

  function typeLabel(mimeType: string): string {
    if (mimeType.startsWith('image/')) return 'IMG';
    if (mimeType.startsWith('video/')) return 'VID';
    if (mimeType.startsWith('audio/')) return 'AUD';
    if (mimeType.startsWith('text/')) return 'TXT';
    if (mimeType.startsWith('application/pdf')) return 'PDF';
    return 'BIN';
  }

  async function loadFiles() {
    if (!user.pubkey) return;
    loading = true;
    error = '';
    try {
      const result = await listFiles(user.pubkey);
      files = result as BlobDescriptor[];
    } catch {
      error = 'Failed to load files';
      files = [];
    } finally {
      loading = false;
    }
  }

  async function copySha256(sha: string) {
    try {
      await navigator.clipboard.writeText(sha);
      copiedSha = sha;
      setTimeout(() => {
        copiedSha = null;
      }, 1500);
    } catch { /* clipboard not available */ }
  }

  async function deleteFile(sha: string) {
    deleting = sha;
    try {
      const auth = await createDeleteAuth(
        (e) => signEvent(e),
        sha,
        { expiration: Math.floor(Date.now() / 1000) + 300 }
      );
      const res = await fetch(`${SERVER}/${sha}`, {
        method: 'DELETE',
        headers: {
          'Authorization': encodeAuthorizationHeader(auth),
        },
      });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
      files = files.filter(f => f.sha256 !== sha);
    } catch (e) {
      error = e instanceof Error ? e.message : 'Delete failed';
      setTimeout(() => { error = ''; }, 3000);
    } finally {
      deleting = null;
      confirmDeleteSha = null;
    }
  }

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      sortDir = sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      sortKey = key;
      sortDir = 'desc';
    }
  }

  function sortIndicator(key: SortKey): string {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' \u2191' : ' \u2193';
  }

  function isImage(mimeType: string): boolean {
    return mimeType.startsWith('image/');
  }

  function isVideo(mimeType: string): boolean {
    return mimeType.startsWith('video/');
  }

  function isPreviewable(mimeType: string): boolean {
    return isImage(mimeType) || isVideo(mimeType);
  }

  function blobUrl(sha: string): string {
    return `${SERVER}/${sha}`;
  }

  function closeViewer() {
    viewerFile = null;
  }

  function openViewer(file: BlobDescriptor) {
    viewerFile = file;
  }

  $effect(() => {
    if (!viewerFile) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeViewer();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  });

  $effect(() => {
    if (user.pubkey) loadFiles();
  });

  let pollTimer: ReturnType<typeof setInterval> | null = null;
  $effect(() => {
    pollTimer = setInterval(() => {
      if (user.pubkey && !loading) loadFiles();
    }, 60000);
    return () => { if (pollTimer) clearInterval(pollTimer); };
  });

  function expiryColor(expiresAt?: number): string {
    if (!expiresAt) return '#484f58';
    const daysLeft = Math.ceil((expiresAt - Date.now() / 1000) / 86400);
    if (daysLeft <= 0) return '#f85149';
    if (daysLeft <= 3) return '#f85149';
    if (daysLeft <= 10) return '#d29922';
    return '#3fb950';
  }

  function expiryLabel(expiresAt?: number): string {
    if (!expiresAt) return '';
    const daysLeft = Math.ceil((expiresAt - Date.now() / 1000) / 86400);
    if (daysLeft <= 0) return 'Expired';
    if (daysLeft === 1) return '1 day left';
    return `${daysLeft} days left`;
  }
</script>

<div>
  <!-- Header -->
  <div class="flex items-center justify-between mb-6">
    <h2 class="text-lg font-semibold text-[#e6edf3]">Your Files</h2>
    <button
      onclick={loadFiles}
      disabled={loading}
      class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#8b949e] hover:text-[#e6edf3] border border-[#21262d] rounded-md hover:border-[#484f58] transition-colors duration-150 disabled:opacity-50"
    >
      <svg class="w-3.5 h-3.5 {loading ? 'animate-spin' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 12a9 9 0 11-6.219-8.56" />
      </svg>
      Refresh
    </button>
  </div>

  {#if error}
    <div class="mb-4 px-4 py-2 bg-[#f851491a] border border-[#f8514933] rounded-md text-sm text-[#f85149]">
      {error}
    </div>
  {/if}

  <!-- Loading skeleton -->
  {#if loading}
    <div class="space-y-2">
      {#each Array(5) as _}
        <div class="flex items-center gap-4 px-4 py-3 border border-[#21262d] rounded-md animate-pulse">
          <div class="w-8 h-8 bg-[#21262d] rounded"></div>
          <div class="flex-1 h-4 bg-[#21262d] rounded"></div>
          <div class="w-16 h-4 bg-[#21262d] rounded"></div>
          <div class="w-24 h-4 bg-[#21262d] rounded"></div>
        </div>
      {/each}
    </div>
  {:else if files.length === 0}
    <!-- Empty state -->
    <div class="text-center py-16 border border-[#21262d] border-dashed rounded-lg">
      <svg class="w-12 h-12 mx-auto text-[#484f58] mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" />
        <polyline points="13 2 13 9 20 9" />
      </svg>
      <p class="text-[#8b949e] mb-4">No files uploaded yet</p>
      <a
        href="#/upload"
        class="inline-flex items-center gap-2 px-4 py-2 bg-[#238636] hover:bg-[#2ea043] text-white rounded-md text-sm font-medium transition-colors duration-150"
      >
        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        Upload a File
      </a>
    </div>
  {:else}
    <!-- Sort controls -->
    <div class="flex items-center gap-2 mb-3 text-xs text-[#8b949e]">
      <span>Sort by:</span>
      {#each [['uploaded', 'Date'], ['size', 'Size'], ['type', 'Type']] as [key, label]}
        <button
          onclick={() => toggleSort(key as SortKey)}
          class="px-2 py-0.5 rounded transition-colors duration-150 {sortKey === key ? 'text-[#58a6ff] bg-[#58a6ff1a]' : 'hover:text-[#e6edf3]'}"
        >
          {label}{sortIndicator(key as SortKey)}
        </button>
      {/each}
    </div>

    <!-- Desktop table -->
    <div class="hidden md:block overflow-x-auto border border-[#21262d] rounded-lg">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-[#21262d] text-left text-[#8b949e] text-xs uppercase tracking-wider">
            <th class="px-4 py-3 w-12">Preview</th>
            <th class="px-4 py-3">SHA-256</th>
            <th class="px-4 py-3 text-right">Size</th>
            <th class="px-4 py-3">Uploaded</th>
            <th class="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {#each sortedFiles as file (file.sha256)}
            <tr class="border-b border-[#21262d] last:border-b-0 hover:bg-[#161b22] transition-colors duration-100">
              <!-- Preview thumbnail -->
              <td class="px-4 py-3">
                {#if isImage(file.type)}
                  <button
                    onclick={() => openViewer(file)}
                    class="block w-10 h-10 rounded overflow-hidden border border-[#21262d] hover:border-[#58a6ff] transition-colors duration-150"
                    title="View image"
                  >
                    <img
                      src={blobUrl(file.sha256)}
                      alt=""
                      loading="lazy"
                      class="w-full h-full object-cover bg-[#161b22]"
                    />
                  </button>
                {:else if isVideo(file.type)}
                  <button
                    onclick={() => openViewer(file)}
                    class="relative flex items-center justify-center w-10 h-10 rounded bg-[#161b22] border border-[#21262d] hover:border-[#bc8cff] transition-colors duration-150"
                    title="Play video"
                  >
                    <svg class="w-4 h-4 text-[#bc8cff]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </button>
                {:else}
                  <span
                    class="inline-flex items-center justify-center w-10 h-10 rounded text-[10px] font-bold"
                    style="background: {typeColor(file.type)}1a; color: {typeColor(file.type)}"
                  >
                    {typeLabel(file.type)}
                  </span>
                {/if}
              </td>
              <!-- SHA-256 -->
              <td class="px-4 py-3">
                <span class="font-mono text-xs text-[#e6edf3]" title={file.sha256}>
                  {file.sha256.slice(0, 16)}...
                </span>
              </td>
              <!-- Size -->
              <td class="px-4 py-3 text-right text-xs tabular-nums text-[#8b949e]">
                {formatSize(file.size)}
              </td>
              <!-- Uploaded -->
              <td class="px-4 py-3 text-xs text-[#8b949e]">
                {relativeTime(file.uploaded)}
              </td>
              <!-- Actions -->
              <td class="px-4 py-3">
                <div class="flex items-center justify-end gap-1">
                  <!-- View (previewable only) -->
                  {#if isPreviewable(file.type)}
                    <button
                      onclick={() => openViewer(file)}
                      class="p-2.5 text-[#8b949e] hover:text-[#58a6ff] rounded transition-colors duration-150"
                      title="View"
                    >
                      <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                  {/if}
                  <!-- Download -->
                  <a
                    href="{SERVER}/{file.sha256}"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="p-2.5 text-[#8b949e] hover:text-[#58a6ff] rounded transition-colors duration-150"
                    title="Download"
                  >
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </a>
                  <!-- Copy SHA -->
                  <button
                    onclick={() => copySha256(file.sha256)}
                    class="p-2.5 text-[#8b949e] hover:text-[#58a6ff] rounded transition-colors duration-150"
                    title="Copy SHA-256"
                  >
                    {#if copiedSha === file.sha256}
                      <svg class="w-4 h-4 text-[#3fb950]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    {:else}
                      <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                      </svg>
                    {/if}
                  </button>
                  <!-- Delete -->
                  {#if confirmDeleteSha === file.sha256}
                    <span class="flex items-center gap-1 text-xs">
                      <button
                        onclick={() => deleteFile(file.sha256)}
                        disabled={deleting === file.sha256}
                        class="px-2 py-0.5 bg-[#f851491a] text-[#f85149] hover:bg-[#f8514933] rounded transition-colors duration-150 disabled:opacity-50"
                      >
                        {deleting === file.sha256 ? 'Deleting...' : 'Confirm'}
                      </button>
                      <button
                        onclick={() => confirmDeleteSha = null}
                        class="px-2 py-0.5 text-[#8b949e] hover:text-[#e6edf3] rounded transition-colors duration-150"
                      >
                        Cancel
                      </button>
                    </span>
                  {:else}
                    <button
                      onclick={() => confirmDeleteSha = file.sha256}
                      class="p-2.5 text-[#8b949e] hover:text-[#f85149] rounded transition-colors duration-150"
                      title="Delete"
                    >
                      <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                      </svg>
                    </button>
                  {/if}
                </div>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <!-- Mobile cards -->
    <div class="md:hidden space-y-3">
      {#each sortedFiles as file (file.sha256)}
        <div class="border border-[#21262d] rounded-lg p-4 hover:border-[#484f58] transition-colors duration-150">
          <div class="flex items-start justify-between gap-3 mb-3">
            <div class="flex items-center gap-3 min-w-0">
              {#if isImage(file.type)}
                <button
                  onclick={() => openViewer(file)}
                  class="flex-shrink-0 w-10 h-10 rounded overflow-hidden border border-[#21262d] hover:border-[#58a6ff] transition-colors duration-150"
                  title="View image"
                >
                  <img
                    src={blobUrl(file.sha256)}
                    alt=""
                    loading="lazy"
                    class="w-full h-full object-cover bg-[#161b22]"
                  />
                </button>
              {:else if isVideo(file.type)}
                <button
                  onclick={() => openViewer(file)}
                  class="flex-shrink-0 relative flex items-center justify-center w-10 h-10 rounded bg-[#161b22] border border-[#21262d] hover:border-[#bc8cff] transition-colors duration-150"
                  title="Play video"
                >
                  <svg class="w-4 h-4 text-[#bc8cff]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
              {:else}
                <span
                  class="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded text-[10px] font-bold"
                  style="background: {typeColor(file.type)}1a; color: {typeColor(file.type)}"
                >
                  {typeLabel(file.type)}
                </span>
              {/if}
              <button
                onclick={() => isPreviewable(file.type) ? openViewer(file) : null}
                class="min-w-0 text-left {isPreviewable(file.type) ? 'cursor-pointer' : 'cursor-default'}"
              >
                <p class="font-mono text-xs text-[#e6edf3] truncate {isPreviewable(file.type) ? 'group-hover:text-[#58a6ff]' : ''}" title={file.sha256}>
                  {file.sha256.slice(0, 16)}...
                </p>
                <p class="text-xs text-[#8b949e] mt-0.5">{file.type || 'unknown'}</p>
              </button>
            </div>
            <div class="flex flex-col items-end gap-0.5">
              <span class="text-xs text-[#8b949e] flex-shrink-0">{relativeTime(file.uploaded)}</span>
              {#if file.expires_at}
                <span class="text-[10px] flex-shrink-0" style="color: {expiryColor(file.expires_at)}">
                  {expiryLabel(file.expires_at)}
                </span>
              {/if}
            </div>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-xs tabular-nums text-[#8b949e]">{formatSize(file.size)}</span>
             <div class="flex items-center gap-2">
                {#if isPreviewable(file.type)}
                  <button
                    onclick={() => openViewer(file)}
                    class="p-2.5 text-[#8b949e] hover:text-[#58a6ff] rounded transition-colors duration-150"
                    title="View"
                  >
                    <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                {/if}
                <a
                  href="{SERVER}/{file.sha256}"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="p-2.5 text-[#8b949e] hover:text-[#58a6ff] rounded transition-colors duration-150"
                  title="Download"
                >
                 <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                   <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                   <polyline points="7 10 12 15 17 10" />
                   <line x1="12" y1="15" x2="12" y2="3" />
                 </svg>
               </a>
               <button
                 onclick={() => copySha256(file.sha256)}
                 class="p-2.5 text-[#8b949e] hover:text-[#58a6ff] rounded transition-colors duration-150"
                 title="Copy SHA-256"
               >
                 {#if copiedSha === file.sha256}
                   <svg class="w-5 h-5 text-[#3fb950]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                     <polyline points="20 6 9 17 4 12" />
                   </svg>
                 {:else}
                   <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                     <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                     <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                   </svg>
                 {/if}
               </button>
               {#if confirmDeleteSha === file.sha256}
                 <span class="flex items-center gap-1 text-xs">
                   <button
                     onclick={() => deleteFile(file.sha256)}
                     disabled={deleting === file.sha256}
                     class="px-3 py-1.5 bg-[#f851491a] text-[#f85149] hover:bg-[#f8514933] rounded transition-colors duration-150 disabled:opacity-50"
                   >
                     {deleting === file.sha256 ? '...' : 'Yes'}
                   </button>
                   <button
                     onclick={() => confirmDeleteSha = null}
                     class="px-3 py-1.5 text-[#8b949e] hover:text-[#e6edf3] rounded transition-colors duration-150"
                   >
                     No
                   </button>
                 </span>
               {:else}
                 <button
                   onclick={() => confirmDeleteSha = file.sha256}
                   class="p-2.5 text-[#8b949e] hover:text-[#f85149] rounded transition-colors duration-150"
                   title="Delete"
                 >
                   <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                     <polyline points="3 6 5 6 21 6" />
                     <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                   </svg>
                 </button>
               {/if}
             </div>
          </div>
        </div>
      {/each}
    </div>

    <!-- File count -->
    <p class="text-xs text-[#484f58] mt-3">{files.length} file{files.length !== 1 ? 's' : ''}</p>
  {/if}
</div>

{#if viewerFile}
  <!-- Media viewer modal -->
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
    role="dialog"
    aria-modal="true"
    aria-label="File preview"
    onclick={closeViewer}
    onkeydown={(e) => { if (e.key === 'Escape') closeViewer(); }}
    tabindex="-1"
  >
    <!-- Close button -->
    <button
      onclick={closeViewer}
      class="absolute top-4 right-4 p-2 text-[#e6edf3] hover:text-white bg-[#21262d]/80 hover:bg-[#30363d] rounded-full transition-colors duration-150"
      title="Close (Esc)"
      aria-label="Close"
    >
      <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>

    <div
      class="relative max-w-4xl max-h-[90vh] w-full flex flex-col items-center justify-center"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => e.stopPropagation()}
      role="document"
    >
      {#if isImage(viewerFile.type)}
        <img
          src={blobUrl(viewerFile.sha256)}
          alt={viewerFile.sha256}
          class="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
        />
      {:else if isVideo(viewerFile.type)}
        <video
          controls
          autoplay
          preload="metadata"
          class="max-w-full max-h-[85vh] rounded-lg shadow-2xl bg-black"
          src={blobUrl(viewerFile.sha256)}
        ></video>
      {:else}
        <div class="w-full bg-[#161b22] border border-[#21262d] rounded-lg p-6 shadow-2xl">
          <div class="flex items-center gap-3 mb-4">
            <span
              class="inline-flex items-center justify-center w-12 h-12 rounded text-xs font-bold"
              style="background: {typeColor(viewerFile.type)}1a; color: {typeColor(viewerFile.type)}"
            >
              {typeLabel(viewerFile.type)}
            </span>
            <div class="min-w-0">
              <p class="text-sm text-[#e6edf3] font-medium truncate">File details</p>
              <p class="text-xs text-[#8b949e]">{viewerFile.type || 'unknown'}</p>
            </div>
          </div>
          <dl class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
            <div>
              <dt class="text-xs text-[#8b949e] uppercase tracking-wider">SHA-256</dt>
              <dd class="font-mono text-xs text-[#e6edf3] mt-1 break-all select-all">{viewerFile.sha256}</dd>
            </div>
            <div>
              <dt class="text-xs text-[#8b949e] uppercase tracking-wider">Size</dt>
              <dd class="text-[#e6edf3] mt-1 tabular-nums">{formatSize(viewerFile.size)}</dd>
            </div>
            <div>
              <dt class="text-xs text-[#8b949e] uppercase tracking-wider">Type</dt>
              <dd class="text-[#e6edf3] mt-1">{viewerFile.type || 'unknown'}</dd>
            </div>
            <div>
              <dt class="text-xs text-[#8b949e] uppercase tracking-wider">Uploaded</dt>
              <dd class="text-[#e6edf3] mt-1">{relativeTime(viewerFile.uploaded)}</dd>
            </div>
          </dl>
          <a
            href={blobUrl(viewerFile.sha256)}
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-2 px-4 py-2 bg-[#238636] hover:bg-[#2ea043] text-white rounded-md text-sm font-medium transition-colors duration-150"
          >
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download
          </a>
        </div>
      {/if}

      <!-- Caption / metadata for media -->
      {#if isPreviewable(viewerFile.type)}
        <div class="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-[#8b949e] font-mono">
          <span>{viewerFile.sha256.slice(0, 16)}...{viewerFile.sha256.slice(-8)}</span>
          <span class="text-[#484f58]">{formatSize(viewerFile.size)}</span>
          <span class="text-[#484f58]">{viewerFile.type}</span>
        </div>
      {/if}
    </div>
  </div>
{/if}
